import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { LOGIN_USER, REQUEST_PASSWORD_RESET } from "../../utils/GraphQL/mutations";
import { Link, useNavigate } from "react-router-dom";
import Auth from "../../utils/auth";
import { useParams } from "react-router-dom";

import "./login.css";
import BdrInput from "../../components/BdrInput/BdrInput";
import BdrButton from "../../components/BdrButton/BdrButton";
import Tracking from "../../components/TrackingComp";
import WebAuthnAuthenticate from "../AdminDashboard/adminComponents/WebAuthn/WebAuthnAuthenticate";

const Login = ({ changeLoggedIn }) => {
  const { paymentID } = useParams();
  const [formState, setFormState] = useState({ email: "", password: "" });
  const [login, { error }] = useMutation(LOGIN_USER);
  const [requestPasswordReset] = useMutation(REQUEST_PASSWORD_RESET);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [errorRes, setErrorRes] = useState("");
  const [errorTemp, setErrorTemp] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [helperMes, setHelperMes] = useState("");
  const [registeredCred, setRegisteredCred] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof PublicKeyCredential !== "undefined") {
      const localIdStored = localStorage.getItem("BDRWebAuthnCredId");
      if (localIdStored) {
        setRegisteredCred(true);
      }
    }
  }, []);
  
  useEffect(() => {
    if (paymentID) {
      console.log("Payment Id: ", paymentID)
      sessionStorage.setItem("bdr-redirect-payment-id", paymentID)
    }
  }, [paymentID]);

  useEffect(() => {
    if (error) {
      setErrorRes(error.message);
      setFailedAttempts((prev) => prev + 1);
    }
  }, [error]);

  useEffect(() => {
    const alreadyLoggedIn = Auth.loggedIn();

    if (alreadyLoggedIn) {
      navigate("/bdr-office/tasks");
    }
  });

  // update state based on form input changes
  const handleChange = (event) => {
    let { name, value } = event.target;

    if (name === "email") {
      value = value.toLowerCase();
    }

    setFormState({
      ...formState,
      [name]: value,
    });
  };

  // submit form
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    try {
      const { data } = await login({
        variables: {
          email: formState.email.trim(),
          password: formState.password.trim(),
        },
      });

      const loginSuccess = Auth.login(data.loginUser.token);

      if (loginSuccess === false) {
        return (
          <p>
            <h1>Sorry, something went wrong</h1>
            <Link to="/">back to the homepage.</Link>
          </p>
        );
      }

      localStorage.setItem("bdr-non-visitor", true);
      // Fresh-login marker for the office clock-in reminder (per-tab; consumed
      // ~60s later by ClockInReminder). A bare refresh never runs this handler,
      // so it stays absent on refresh.
      sessionStorage.setItem("bdr-clockin-fresh-login", "1");

      await changeLoggedIn(true);

      navigate("/bdr-office/tasks");
    } catch (err) {
      console.error(err.message);
    }

    // clear form values
    setFormState((prev) => ({
      email: prev.email,
      password: "",
    }));
  };

  const clearError = () => {
    setErrorRes("");
    setErrorTemp("");
  };

  const sendTempPass = async () => {
    clearError();
    // Token-based reset. Always show the SAME neutral message regardless of
    // outcome, so the form can't be used to discover which emails are
    // registered. The emailed code/link lands on /reset.
    try {
      await requestPasswordReset({
        variables: { email: formState.email.trim().toLowerCase() },
      });
    } catch (err) {
      // Swallow — neutral response either way.
    }
    setHelperMes(
      "If an account exists for that email, a password reset code has been sent."
    );
  };

  return (
    <div className="login-box">
      {registeredCred ? (
        <div className="login-card">
          <div className="login-actions">
            <WebAuthnAuthenticate changeLoggedIn={changeLoggedIn} />
            <BdrButton
              variant="action-solid"
              className="login-btn-full"
              onClick={() => setRegisteredCred(false)}
            >
              Login with Credentials
            </BdrButton>
          </div>
        </div>
      ) : (
        <div className="login-card">
          <form
            autoComplete="on"
            action=""
            method=""
            className="login-form"
            onSubmit={handleFormSubmit}
          >
            <BdrInput
              label="Email"
              placeholder="you@example.com"
              name="email"
              type="email"
              value={formState.email}
              onChange={handleChange}
              onFocus={clearError}
              autoComplete="email"
              inputClassName="bdr-input--autofill-dark"
            />
            <BdrInput
              label="Password"
              placeholder="••••••••"
              name="password"
              type={passwordVisible ? "text" : "password"}
              value={formState.password}
              onChange={handleChange}
              onFocus={clearError}
              autoComplete="current-password"
              inputClassName="bdr-input--autofill-dark login-password"
              trailing={
                <BdrButton
                  variant="plain"
                  size="sm"
                  className="login-show-toggle"
                  onClick={() => setPasswordVisible((v) => !v)}
                  ariaLabel={passwordVisible ? "Hide password" : "Show password"}
                  aria-pressed={passwordVisible}
                >
                  {passwordVisible ? "Hide" : "Show"}
                </BdrButton>
              }
            />
            {/* type="submit" is load-bearing: it is the form's ONLY submit
                control, so without it neither Enter nor a click logs in. */}
            <BdrButton type="submit" variant="confirm-solid" className="login-btn-full">
              Login
            </BdrButton>
          </form>
          {failedAttempts > 1 && (
            <BdrButton size="sm" className="login-reset-btn" onClick={() => sendTempPass()}>
              Reset password?
            </BdrButton>
          )}
          {helperMes && (
            <p className="bdr-alert bdr-alert--success" role="status">
              {helperMes}
            </p>
          )}
          {errorTemp && (
            <p className="bdr-alert bdr-alert--danger" role="alert">
              {errorTemp}
            </p>
          )}
          {!errorTemp && errorRes && (
            <p className="bdr-alert bdr-alert--danger" role="alert">
              {errorRes}
            </p>
          )}
        </div>
      )}
      <Tracking removeVisit={true} />
    </div>
  );
};

export default Login;
